/**
 * module-permissions-api — bound wrappers for the module-permission RPCs (migration 141).
 * Permissions are read and written ONLY through RPCs.
 */
import { supabase } from '@/integrations/supabase/client';

export type PermLevel = 'none' | 'view' | 'edit';

export interface ModulePermission { module_key: string; label: string; level: PermLevel; kind?: string | null }
export interface UserModulePermission {
  module_key: string; label: string; admin_only: boolean; kind?: string | null;
  preset_level: PermLevel | null; override_level: PermLevel | null; effective_level: PermLevel;
}
export interface PresetCell { preset_key: string; module_key: string; level: PermLevel }

interface RpcResult { data: unknown; error: { message: string } | null }
interface LooseClient {
  rpc: (fn: string, args?: Record<string, unknown>) => PromiseLike<RpcResult>;
  from: (t: string) => { select: (c: string) => PromiseLike<{ data: unknown; error: { message: string } | null }> };
}
const client = supabase as unknown as LooseClient;

async function callRpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await client.rpc(fn, args);
  if (error) throw new Error(error.message || `${fn} failed`);
  return data as T;
}

export const PRESET_KEYS = ['staff', 'manager', 'bod_readonly'] as const;
export const PRESET_LABELS: Record<string, string> = { staff: 'Staff', manager: 'Manager', bod_readonly: 'BOD view only' };

export const modulePermissionsApi = {
  myPermissions: () => callRpc<ModulePermission[]>('my_module_permissions').then(r => r ?? []),
  getUserPermissions: (p_user_id: string) =>
    callRpc<UserModulePermission[]>('admin_get_user_permissions', { p_user_id }).then(r => r ?? []),
  setUserPreset: (p_user_id: string, p_preset_key: string) =>
    callRpc<void>('admin_set_user_preset', { p_user_id, p_preset_key }),
  setUserOverride: (p_user_id: string, p_module_key: string, p_level: PermLevel | null) =>
    callRpc<void>('admin_set_user_override', { p_user_id, p_module_key, p_level }),
  setPresetPermission: (p_preset_key: string, p_module_key: string, p_level: PermLevel) =>
    callRpc<void>('admin_set_preset_permission', { p_preset_key, p_module_key, p_level }),
  /** Read-only: current preset matrix (SELECT only; writes go through admin_set_preset_permission). */
  presetMatrix: async (): Promise<PresetCell[]> => {
    const { data, error } = await client.from('access_preset_permissions').select('preset_key, module_key, level');
    if (error) throw new Error(error.message);
    return (data as PresetCell[]) ?? [];
  },
};
